import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FocusTrap } from 'focus-trap-react';
import queryString from 'query-string';

import { AddPetForm } from './AddPetForm';

import {
  isProp103FeaturesForActions,
  useBreedsByState,
  useOnEscEvent,
  usePrevious,
  useProp103Features,
  useQuoteTotal,
  useSavedElement,
  useTranslations,
} from '../../../hooks';

import {
  addPet,
  loadAgeData,
  loadBreedData,
  noDiamondClientIdError,
  saveNewPetBreed,
  saveNewPetEmail,
  saveNewPetName,
  saveNewPetZipCode,
  saveSelectedEditPetModal,
  saveUpdatedPet,
  updatePet,
  validateIfEmailExists,
  validZipCode,
} from '../../../actions/quoting';
import {
  removeEmojis,
  testOnlyNumber,
  validateEmail,
} from '../../../util';
import {
  MAX_PET_NAME_LENGTH,
  PetGender,
  PetType,
  POWERUP_TYPE,
  PROP103_PLAN_TYPE,
} from '../../../constants';

import './ModalAddPet.css';
import { getStateByZipCode } from '../../../utils/zipcodes.utils';

const ModalAddPet = ({
  getQuote = false,
  handleCloseModalAddPet = () => { },
  isEditing = false,
  isSecondStep = false,
  registerPriceToCompare = () => { },
  show = false,
  validateOnMount = false,
}) => {
  const location = useLocation();
  const [firstMount, setFirstMount] = useState(true);
  const [petNameFocusFlag, setPetNameFocusFlag] = useState(false);
  const [petTypeError, setPetTypeError] = useState('');
  const [petGenderError, setPetGenderError] = useState('');
  const [petNameError, setPetNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [zipCodeError, setZipcodeError] = useState('');
  const [ageError, setAgeError] = useState(false);
  const [breedError, setBreedError] = useState(false);
  const [validating, setValidating] = useState(false);
  const [termsPolicyError, setTermsPolicyError] = useState(false);
  const [isFocusTrapActive, setIsFocusTrapActive] = useState(false);
  const breedsByState = useBreedsByState();
  const isProp103 = useProp103Features();
  const quoteTotal = useQuoteTotal();

  function handleKeyDown(event) {
    const TAB_KEY = 9;

    if (event.keyCode === TAB_KEY && !getQuote) {
      setIsFocusTrapActive(true);
    }
  }

  const store = useSelector((stores) => stores.quoting);
  const {
    acceptedTermsAndPolicy,
    ageData,
    data,
    effectiveDateCustom,
    employerId,
    employerPromoCode,
    guidData,
    parameters,
    petQuoteSelected,
    sessionInformation,
    subId,
    newPet: {
      email,
      petAge,
      petBreed,
      petBreedId,
      petName,
      petSex,
      petType,
      zipcode,
    },
    petUpdated,
    customBreedError,
    customerZipCode,
  } = store;

  const { t } = useTranslations('landing');
  const { t: tQuoting } = useTranslations('quoting');

  const dispatch = useDispatch();
  const dogSpeciesRef = useRef(null);
  const maleGenderRef = useRef(null);
  const petNameRef = useRef(null);
  const petAgeRef = useRef(null);
  const petBreedRef = useRef(null);
  const petZipcodeRef = useRef(null);
  const petEmailRef = useRef(null);

  const { goToSavedElementAndForgot } = useSavedElement();

  useEffect(() => {
    if (firstMount && show) {
      if (breedsByState && zipcode) {
        dispatch(loadBreedData(zipcode));
      } else {
        dispatch(loadBreedData());
      }
      dispatch(loadAgeData());
      setFirstMount(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, firstMount, show]);

  useEffect(() => {
    setBreedError(customBreedError);
  }, [customBreedError]);

  function getQuteButtonEnabled() {
    if (!ageData) {
      return false;
    }

    return (parameters.email || email) && zipcode && petName
      && petBreed && petBreedId && petAge && petAge !== ageData[0].Description
      && !petNameError && !emailError && !zipCodeError
      && !petTypeError && petType.value !== PetType.None.value
      && !petGenderError && petSex.value !== PetGender.None.value;
  }

  const getBodyForQuote = useCallback(async (isEditingPet) => {
    const { ebPetQuoteResponseList } = data;

    // pets from rate quote response (now including URL pet)
    let currentPets = ebPetQuoteResponseList
      .map((petQuote) => (
        {
          cloudOrderId: petQuote.cloudOrderId || 0,
          Id: petQuote.petQuoteId,
          Modifiers: petQuote.modifiers,
          PetAge: petQuote.petAgeName,
          PetBreed: petQuote.breedName,
          PetBreedId: petQuote.breedId,
          PetName: petQuote.petName,
          PetSex: petQuote.genderName,
          PetType: petQuote.petType === PetType.Dog.value
            ? PetType.Dog.value : PetType.Cat.value,
          PlanType: petQuote.PlanType,
        }
      ));

    const newPetIdAdded = ebPetQuoteResponseList[
      ebPetQuoteResponseList.length - 1].petQuoteId + 1;

    if (!isEditingPet) {
      // current pet for add
      const { insuranceProductEB: { InsuranceModifiersEB } } = data;
      const modifiers = InsuranceModifiersEB
        .filter(
          (modifier) => modifier.InsuranceModifierTypeEBId === POWERUP_TYPE
            && modifier.IsVisible,
        )
        .map((item) => ({
          id: item.Id,
          isSelected: null,
        }));

      const anyModifierSelected = modifiers.some((item) => item.isSelected);

      currentPets.push({
        Id: newPetIdAdded,
        // ECP = Extra Care Pack Or Enrique Peña Nieto
        // Send empty array if no power up selected (is neccesary for ECP)
        Modifiers: anyModifierSelected ? modifiers : [],
        PetAge: petAge,
        PetBreed: petBreed,
        PetBreedId: petBreedId,
        PetName: petName,
        PetSex: petSex.name,
        PetType: petType.name === PetType.Dog.name
          ? PetType.Dog.value : PetType.Cat.value,
        PlanType: isProp103
          ? PROP103_PLAN_TYPE.ACCIDENT_AND_ILLNESS : null,
      });
    }

    const quoteId = isEditingPet ? petQuoteSelected : newPetIdAdded;

    let willBeProp103 = isProp103;

    if (parameters.petZipCode !== customerZipCode) {
      const zipCodeData =
        await getStateByZipCode(parameters.petZipCode);
      willBeProp103 = isProp103FeaturesForActions(zipCodeData.stateAbbr);

      // Switching from no Prop103 to Prop103 state
      if (!isProp103 && willBeProp103) {
        currentPets = currentPets.map((pet) => ({
          ...pet,
          PlanType: PROP103_PLAN_TYPE.ACCIDENT_AND_ILLNESS,
        }));
      } else if (isProp103 && !willBeProp103) {
        currentPets = currentPets.map((pet) => ({
          ...pet,
          PlanType: null,
        }));
      }
    }

    const body = {
      EffectiveDate: null,
      GroupCode: employerPromoCode,
      GroupCodeDscr: parameters.groupCodeDscr,
      petQuotes: currentPets,
      quoteId,
      QuoteSubId: subId,
      TestEffectiveDate: effectiveDateCustom,
      ZipCode: parameters.petZipCode,
    };

    return body;
  }, [
    data,
    effectiveDateCustom,
    employerPromoCode,
    parameters.groupCodeDscr,
    parameters.petZipCode,
    petAge,
    petBreed,
    petBreedId,
    petName,
    petQuoteSelected,
    petSex.name,
    petType.name,
    subId,
    isProp103,
    customerZipCode,
  ]);
  const history = useHistory();

  const ageRequiredError = useMemo(() => tQuoting('petForm.errors.required', {
    field: tQuoting('petForm.age'),
  }), [tQuoting]);

  const breedRequiredError = useMemo(() => tQuoting('petForm.errors.required', {
    field: tQuoting('petForm.breed'),
  }), [tQuoting]);

  const updatePetRate = useCallback(async () => {
    const { nopCommerceUser, userEmail } = sessionInformation;
    const partialBody = await getBodyForQuote(true);
    const body = {
      ...partialBody,
      DiamondClientId: nopCommerceUser.DiamonClientdId,
      ebGuID: guidData.GuID,
      eMail: userEmail || parameters.email,
      employerName: guidData.Name,
      nopCommerceClientId: nopCommerceUser.CustomerNopCommerceId,
    };
    dispatch(updatePet({
      body,
      update: isEditing,
    }));
    handleCloseModalAddPet();
    setFirstMount(true);
  }, [
    dispatch,
    getBodyForQuote,
    guidData.GuID,
    guidData.Name,
    handleCloseModalAddPet,
    isEditing,
    parameters.email,
    sessionInformation,
  ]);

  useEffect(() => {
    if (petUpdated) {
      updatePetRate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [petUpdated]);

  useOnEscEvent(() => {
    handleCloseModalAddPet();
    setTimeout(goToSavedElementAndForgot, 0.01);
  }, []);

  useEffect(() => {
    setAgeError('');
  }, [petAge]);

  const prevBreed = usePrevious(petBreed);
  useEffect(() => {
    setBreedError(!!prevBreed && !petBreed ? breedRequiredError : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [petBreed]);

  const handlePetNameChange = useCallback((event) => {
    setPetNameError('');
    const petNameTyped = removeEmojis(event.target.value);
    if (petNameTyped.length <= MAX_PET_NAME_LENGTH) {
      dispatch(saveNewPetName(petNameTyped));
    }
  }, [dispatch]);

  const handleZipcodeChange = useCallback((event) => {
    const { value } = event.target;
    const newValue = testOnlyNumber(value) ? value : zipcode;
    // reset breed selection if zipcode changes
    if (breedsByState && petBreed) {
      dispatch(saveNewPetBreed({
        petBreed: null,
        petBreedId: null,
      }));
      setBreedError(breedRequiredError);
    }

    dispatch(saveNewPetZipCode(newValue));
  }, [breedsByState, dispatch, petBreed, zipcode, breedRequiredError]);

  const handleEmail = useCallback((event) => {
    const { value } = event.target;

    dispatch(saveNewPetEmail(removeEmojis(value)));
    setEmailError('');
  }, [dispatch]);

  const handleOnCancelClick = useCallback(() => {
    handleCloseModalAddPet();
    dispatch(saveSelectedEditPetModal(false));
    setTimeout(goToSavedElementAndForgot, 0.01);
  }, [dispatch, goToSavedElementAndForgot, handleCloseModalAddPet]);

  const isValidZipCode = useCallback(async () => {
    const zipValid = await validZipCode(zipcode)();

    return zipValid;
  }, [zipcode]);

  const onBlurEmail = useCallback(() => {
    let message = 'Email is required';

    if (email) {
      message = validateEmail(email) ? '' : 'Invalid email';
    }

    setEmailError(message);
  }, [email]);

  const areTermsRejected = useCallback(() => {
    setTermsPolicyError(!acceptedTermsAndPolicy);
    return !acceptedTermsAndPolicy;
  }, [acceptedTermsAndPolicy]);

  const handleEnterPress = useCallback((event) => {
    if (event.key === 'Enter') {
      onBlurEmail();
    }
  }, [onBlurEmail]);

  const onBlurZipcode = useCallback(async () => {
    let message = 'Zip code is required';

    if (zipcode) {
      if (breedsByState) {
        dispatch(loadBreedData(zipcode));
      }

      if (zipcode.length < 5) {
        message = 'Invalid zip code';
      } else {
        const zipCodeValidate = await isValidZipCode();

        message = zipCodeValidate ? '' : 'Invalid zip code';
      }
    }

    setZipcodeError(message);
  }, [breedsByState, dispatch, isValidZipCode, zipcode]);

  const onBlurPetName = useCallback(() => {
    const newPetName = petName.split(' ').filter((item) => !!item).join(' ');
    dispatch(saveNewPetName(newPetName));
    const message = petName.length ? '' : 'Pet\'s name is required';
    setPetNameError(message);
    setPetNameFocusFlag(false);
  }, [dispatch, petName]);

  function setFocusOnErrorField() {
    if (!petName && petNameRef.current) {
      petNameRef.current.focus();
      return;
    }

    if (petType.value === PetType.None.value && dogSpeciesRef.current) {
      dogSpeciesRef.current.focus();
      return;
    }

    if (petSex.value === PetGender.None.value && maleGenderRef.current) {
      maleGenderRef.current.focus();
      return;
    }

    if (!petAge && petAgeRef.current) {
      petAgeRef.current.focus();
      return;
    }

    if (!petBreed && petBreedRef.current) {
      petBreedRef.current.focus();
      return;
    }

    if ((!zipcode || zipCodeError) && petZipcodeRef.current) {
      petZipcodeRef.current.focus();
      return;
    }

    if (!email && petEmailRef.current) {
      petEmailRef.current.focus();
    }
  }

  function doFormValidations() {
    setAgeError(!petAge ? ageRequiredError : '');
    setBreedError(!petBreed ? breedRequiredError : '');
    onBlurPetName();
    onBlurZipcode();
    onBlurEmail();
    setPetTypeError(petType.value === PetType.None.value
      ? 'Pet type must be selected in order to proceed' : '');
    setPetGenderError(petSex.value === PetGender.None.value
      ? 'Pet gender must be selected in order to proceed' : '');
    areTermsRejected();

    setFocusOnErrorField();
  }

  useEffect(() => {
    if (getQuote && validateOnMount) {
      doFormValidations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getQuote, validateOnMount]);

  function validateFields() {
    return petName
      && (petSex.value !== PetType.None.value)
      && petBreed
      && petBreedId
      && petAge
      && (zipcode && !zipCodeError)
      && (ageData && petAge !== ageData[0].Description);
  }

  async function handleOnSaveClick() {
    const { sessionInformation: { nopCommerceUser, userEmail } } = store;

    if (handleCloseModalAddPet) {
      doFormValidations();

      if (validateFields()) {
        window.scrollTo(0, 0);

        if (isEditing) {
          const zipValid = await isValidZipCode();

          if (!zipValid) {
            setZipcodeError('Invalid zip code');

            return;
          }

          dispatch(saveUpdatedPet());
        } else {
          const body = await getBodyForQuote(isEditing);
          const bodyWithDiamondClientId = {
            ...body,
            DiamondClientId: nopCommerceUser.DiamonClientdId,
            ebGuID: guidData.GuID,
            eMail: userEmail || parameters.email,
            employerName: guidData.Name,
            nopCommerceClientId: nopCommerceUser.CustomerNopCommerceId,
          };
          dispatch(addPet(bodyWithDiamondClientId));
          handleCloseModalAddPet();
          setFirstMount(true);
        }
      }
    }
  }

  async function onClickGetQuote() {
    if (validating) {
      return;
    }

    setValidating(true);

    if (!getQuteButtonEnabled()) {
      doFormValidations();
      setValidating(false);
      return;
    }

    const zipValid = await isValidZipCode();

    if (!zipValid) {
      setZipcodeError('Invalid zip code');

      setValidating(false);
      return;
    }

    if (areTermsRejected()) {
      setValidating(false);
      return;
    }

    const { query } = queryString.parseUrl(location.search);
    const agentIdParam = query.agentId ? { agentId: query.agentId } : {};

    const quote = {
      ...agentIdParam,
      email,
      empGuid: employerId,
      id: '1',
      petAge,
      petBreed,
      petBreedId,
      petName,
      petSex: petSex.name,
      petType: petType.name,
      petZipCode: zipcode,
    };

    if (subId && subId.Name && subId.Value) {
      quote[subId.Name] = subId.Value;
    }

    const payload = await validateIfEmailExists(email);

    const { userAlreadyExists, hasCustomerRoll, hasPassword } = payload;

    if (userAlreadyExists && !hasCustomerRoll) {
      // users with no roll or diamondCliendId: 0 need call customer service.
      // WPI: Petclout Free user?
      dispatch(noDiamondClientIdError('User without customer roll'));
      setValidating(false);
      return;
    }

    const quoteBase = queryString.stringify(quote);
    const urlQuoting = `/quoting/getQuote?${quoteBase}`;

    setValidating(false);

    if (userAlreadyExists) {
      const urlLogin = `/login?${quoteBase}`;
      history.push(urlLogin, { hasPassword: hasPassword ?? true });
    } else {
      history.push(urlQuoting);
    }
  }

  const handleSubmitPetForm = (event) => {
    event.preventDefault();

    if (getQuote) {
      onClickGetQuote();
      return;
    }

    window.scrollTo(0, 0);
    registerPriceToCompare(quoteTotal);
    handleOnSaveClick();
  };

  function getTitle() {
    if (getQuote) {
      return (
        <div className="Modal-add-pet-title-container">
          <span>{t('modalAddPet.getQuote')}</span>
        </div>
      );
    }

    return isEditing ? t('modalAddPet.editPet') : t('modalAddPet.addPet');
  }

  useEffect(() => {
    if (!getQuote) {
      window.addEventListener('keydown', handleKeyDown, true);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      setIsFocusTrapActive(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getQuote]);

  const description = useMemo(() => {
    if (!isEditing) {
      return tQuoting('petForm.addPet');
    }
    if (isSecondStep) {
      return tQuoting('petForm.editPetYourInfo');
    }
    return '';
  }, [isEditing, isSecondStep, tQuoting]);

  return (
    <FocusTrap active={isFocusTrapActive}>
      <div
        className={'Modal-add-pet-content'
          + ` ${getQuote ? 'Modal-add-pet-content-landing' : ''}`}
      >
        <h1 className="Modal-add-pet-title">
          {getTitle()}
        </h1>

        <hr aria-hidden className="Modal-add-pet-title-separator" />

        <span
          className={'Modal-add-pet-title-description'
            + `${isEditing && !isSecondStep ? ' Gone' : ''}`}
        >
          {description}
        </span>

        <AddPetForm
          ageError={ageError}
          breedError={breedError}
          emailError={emailError}
          getQuote={getQuote}
          handleEmail={handleEmail}
          handleOnCancelClick={handleOnCancelClick}
          handlePetNameChange={handlePetNameChange}
          handleSubmitPetForm={handleSubmitPetForm}
          handleZipcodeChange={handleZipcodeChange}
          isEditing={isEditing}
          onBlurEmail={onBlurEmail}
          onBlurPetName={onBlurPetName}
          onBlurZipcode={onBlurZipcode}
          onKeyPress={handleEnterPress}
          petAgeRef={petAgeRef}
          petBreedRef={petBreedRef}
          petEmailRef={petEmailRef}
          petGenderError={petGenderError}
          petGenderRef={maleGenderRef}
          petNameError={petNameError}
          petNameFocusFlag={petNameFocusFlag}
          petNameRef={petNameRef}
          petTypeError={petTypeError}
          petTypeRef={dogSpeciesRef}
          petZipcodeRef={petZipcodeRef}
          setPetGenderError={setPetGenderError}
          setPetTypeError={setPetTypeError}
          setTermsPolicyError={setTermsPolicyError}
          show={show}
          termsPolicyError={termsPolicyError}
          zipCodeError={zipCodeError}
        />
      </div>
    </FocusTrap>
  );
};

export { ModalAddPet };
